
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { ForgotPasswordModal } from './ForgotPasswordModal';
import { track } from '@/lib/analytics';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { sanitizeDashboardReturnPath } from '@/utils/dashboardNavigation';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const { signIn } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await signIn(email.trim().toLowerCase(), password);
      try {
        track('login');
      } catch {
        // A tracking failure must not skip the signed-in navigation.
      }
      toast({
        title: 'Welcome back!',
        description: 'You have been signed in successfully.',
      });
      onClose();
      const target =
        sanitizeDashboardReturnPath(searchParams.get('next')) ?? '/dashboard';
      navigate(target);
    } catch (err) {
      // Persistent, announced error (ERI-1632) — a toast auto-dismisses, so SR/keyboard
      // users who miss the single announcement can't re-find it.
      setError(err instanceof Error ? err.message : 'Unable to sign in. Please check your email and password.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    setShowForgotPassword(true);
  };

  const handleBackToSignIn = () => {
    setShowForgotPassword(false);
  };

  const handleModalClose = () => {
    setShowForgotPassword(false);
    onClose();
  };

  return (
    <>
      <Dialog open={isOpen && !showForgotPassword} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Sign In to Your Premium Account</DialogTitle>
            <DialogDescription>
              Sign in to access your premium planning tools.
            </DialogDescription>
          </DialogHeader>
          {error && (
            <p id="signin-error" role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                aria-invalid={!!error}
                aria-describedby={error ? 'signin-error' : undefined}
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                aria-invalid={!!error}
                aria-describedby={error ? 'signin-error' : undefined}
              />
            </div>
            <Button type="submit" disabled={loading} className="w-full">
              {loading ? 'Loading...' : 'Sign In'}
            </Button>
          </form>
          
          <div className="text-center">
            <Button 
              onClick={handleForgotPassword}
              variant="link" 
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              Forgot your password?
            </Button>
          </div>
          
          <div className="text-center text-sm text-muted-foreground">
            Don't have an account?{' '}
            <Link
              to="/register"
              onClick={onClose}
              className="text-foreground underline underline-offset-2 hover:text-foreground/80"
            >
              Create one
            </Link>
          </div>
        </DialogContent>
      </Dialog>

      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={handleModalClose}
        onBackToSignIn={handleBackToSignIn}
      />
    </>
  );
};
